import IconButtonTsoPq from '@components/Buttons/IconButtonTsoPq';
import CollapseButtonTsoPq from '@components/CollapseButton/CollapseButtonTsoPq';
import TreeItemViewTspPq from '@components/ListItem/TreeListItemTsoPq';
import ModalDialogTsoPq from '@components/ModalDialog/ModalDialogTsoPq';
import SearchFieldTsoPq from '@components/SearchField/SearchFieldTsoPq';
import { TreeListItemTsoPqProps } from '@custom-types/TreeTypes';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { useDeleteMeasurementDeviceByIdMutation } from '@redux/apiSlices/measurementDeviceApi';
import { useDeleteMeasurementPointByIdMutation } from '@redux/apiSlices/measurementPointApi';
import {
  ApiDatabaseDtOsSubstationsDeviceHierarchyDto,
  useDeleteSubstationByIdMutation,
  useGetSubstationGetSubstationDeviceHierarchyQuery,
} from '@redux/apiSlices/substationApi';
import { useGetTsoGetAllQuery } from '@redux/apiSlices/tsoApi';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import { setSnackbar } from '@redux/slices/snackbarSlice';
import { skipToken } from '@reduxjs/toolkit/query';
import { findTreePath } from '@utils/findTreePath';
import { useFlattenTreeProp, useFlattenTreeProps } from '@utils/hooks/useFlattenTreeProp';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router';
import MeasurmentDevice from './ManagementSections/MeasurementDevice';
import MeasurementPoint from './ManagementSections/MeasurementPoint';
import Substation from './ManagementSections/Substation';
import {
  StyledContent,
  StyledContentTitle,
  StyledContentWrapper,
  StyledHeaderContainer,
  StyledManagementModals,
  StyledSearchContainer,
  typeUrl,
} from './styledManagementPage';
import { selectExpandedNodes, selectSearchTerm, setExpandedNodes, setSearchTerm } from '@redux/slices/managementTreeSlice';
import { SidePanelPageLayoutTsoPq } from '@components/Grid/GridTsoPq';
import { SidePanelTsoPq } from '@components/Grid/SidePanelTsoPq';
import TypographyTsoPq from '@components/Typography/TypographyTsoPq';
import ImagePlaceholderTsoPq from '@components/ImagePlaceholder/ImagePlaceholderTsoPq';
import { Box } from '@mui/material';
import { RouteMap } from '@constants/routes';
import PermissionGate from '@components/PermissionGate/PermissionGate';
import { userOrSupportRestriction } from '@components/PermissionGate/RolePermissionConfig';

type ModalContentType = 'newSubstation' | 'newMeasurmentPoint' | 'newMeasurmentDevice' | undefined;
type PageContentType = 'substation' | 'measurmentPoint' | 'measurmentDevice' | undefined;

const typeMap: Record<number, 'substation' | 'measurmentPoint' | 'measurmentDevice'> = {
  0: 'substation',
  1: 'measurmentPoint',
  2: 'measurmentDevice',
};

const ManagementPage = () => {
  const dispatch = useAppDispatch();
  const expandedNodes = useAppSelector(selectExpandedNodes);
  const searchTerm = useAppSelector(selectSearchTerm);
  const [searchKey, setSearchKey] = useState<'label' | 'id'>('label');
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [modalContent, setModalContent] = useState<ModalContentType>(undefined);
  const [pageContent, setPageContent] = useState<PageContentType>(undefined);
  const [treePath, setTreePath] = useState<{ id: string; entityId: number; label: string }[] | undefined>();
  const [options, setOptions] = useState<TreeListItemTsoPqProps[] | undefined>(undefined);

  const { data: tsos } = useGetTsoGetAllQuery();

  const { data: deviceHierarchy, refetch } = useGetSubstationGetSubstationDeviceHierarchyQuery(
    tsos?.externalDtos?.find((t: { isLoggedInUsersTso: any }) => t.isLoggedInUsersTso)?.id ?? skipToken
  );

  const [deleteSubstation] = useDeleteSubstationByIdMutation();
  const [deleteMeasurementPoint] = useDeleteMeasurementPointByIdMutation();
  const [deleteMeasuringDevice] = useDeleteMeasurementDeviceByIdMutation();

  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const resetSearch = () => setExpandedNodes([]);
  const treeIds = useFlattenTreeProp(options ?? [], 'id');
  const flatTreeIds = useFlattenTreeProps(options ?? [], ['id', 'entityId', 'type']) as {
    id: string;
    entityId: number;
    type: string;
  }[];
  const { type, deviceId } = useParams();

  const resetAfterDelete = () => {
    setTreePath(undefined);
    setPageContent(undefined);
    setExpandedNodes([]);
    setOpenModal(false);
    navigate(RouteMap.ADMINISTRATION_ROUTE);
  };
  const handleDeleteConfirmMeasuringDevice = async () => {
    const mdId = treePath?.at(-1)?.entityId;
    if (!mdId) return;

    try {
      await deleteMeasuringDevice(mdId).unwrap();

      dispatch(
        setSnackbar({
          open: true,
          type: 'success',
          textContent: t('snackbar.measurementDevice.delete.success'),
          copyToClipboard: false,
          requestID: undefined,
        })
      );

      await refetch();
      resetAfterDelete();
    } catch (error: any) {
      console.error('Error deleting measuring device:', error);
      const requestID = error?.data?.traceId || undefined;

      dispatch(
        setSnackbar({
          open: true,
          type: 'error',
          textContent: t('snackbar.measurementDevice.delete.error'),
          copyToClipboard: true,
          requestID,
        })
      );
    }
  };

  const handleDeleteConfirmMeasurementPoint = async () => {
    const mpId = treePath?.at(-1)?.entityId;
    if (!mpId) return;

    try {
      await deleteMeasurementPoint(mpId).unwrap();

      dispatch(
        setSnackbar({
          open: true,
          type: 'success',
          textContent: t('snackbar.measurementPoint.delete.success'),
          copyToClipboard: false,
          requestID: undefined,
        })
      );

      await refetch();
      resetAfterDelete();
    } catch (error: any) {
      console.error('Error deleting measurement point:', error);
      const requestID = error?.data?.traceId || undefined;

      dispatch(
        setSnackbar({
          open: true,
          type: 'error',
          textContent: t('snackbar.measurementPoint.delete.error'),
          copyToClipboard: true,
          requestID,
        })
      );
    }
  };

  const handleDeleteConfirmMSubstation = async () => {
    const substationId = treePath?.at(-1)?.entityId;
    if (!substationId) return;

    try {
      await deleteSubstation(substationId).unwrap();

      dispatch(
        setSnackbar({
          open: true,
          type: 'success',
          textContent: t('snackbar.substation.delete.success'),
          copyToClipboard: false,
          requestID: undefined,
        })
      );

      setOpenModal(false);
      setPageContent(undefined);
      resetAfterDelete();
    } catch (error: any) {
      console.error('Error deleting substation:', error);
      const requestID = error?.data?.traceId || undefined;

      dispatch(
        setSnackbar({
          open: true,
          type: 'error',
          textContent: t('snackbar.substation.delete.error'),
          copyToClipboard: true,
          requestID,
        })
      );
    }
  };

  const contentMap = {
    modal: {
      newSubstation: (
        <Substation
          newSubstation
          handleDiscard={() => setOpenModal(false)}
          refetch={refetch}
          deleteTitle={t('management.measurementPoint.substationDeleteTitle')}
          deleteText={t('management.measurementPoint.substationDeleteContent')}
        />
      ),
      newMeasurmentPoint: (
        <MeasurementPoint
          newMeasurementPoint
          handleDiscard={() => setOpenModal(false)}
          treePath={treePath}
          refetch={refetch}
          deleteTitle={t('management.measurementPoint.measurementPointDeleteTitle')}
          deleteText={t('management.measurementPoint.measurementPointDeleteContent')}
        />
      ),
      newMeasurmentDevice: (
        <MeasurmentDevice
          newMeasurmentDevice
          handleDiscard={() => setOpenModal(false)}
          treePath={treePath}
          refetch={refetch}
          deleteTitle={t('management.measurementDevice.measurementDeviceDeleteTitle')}
          deleteText={t('management.measurementDevice.measurementDeviceDeleteContent')}
        />
      ),
    },
    page: {
      substation: (
        <Substation
          refetch={refetch}
          deleteTitle={t('management.measurementPoint.substationDeleteTitle')}
          deleteText={t('management.measurementPoint.substationDeleteContent')}
          onDeleteConfirm={handleDeleteConfirmMSubstation}
        />
      ),
      measurmentPoint: (
        <MeasurementPoint
          treePath={treePath}
          refetch={refetch}
          deleteTitle={t('management.measurementPoint.measurementPointDeleteTitle')}
          deleteText={t('management.measurementPoint.measurementPointDeleteContent')}
          onDeleteConfirm={handleDeleteConfirmMeasurementPoint}
        />
      ),
      measurmentDevice: (
        <MeasurmentDevice
          treePath={treePath}
          refetch={refetch}
          deleteTitle={t('management.measurementDevice.measurementDeviceDeleteTitle')}
          deleteText={t('management.measurementDevice.measurementDeviceDeleteContent')}
          onDeleteConfirm={handleDeleteConfirmMeasuringDevice}
        />
      ),
    },
  };

  const convertDto = (
    tree: ApiDatabaseDtOsSubstationsDeviceHierarchyDto[] | null | undefined
  ): TreeListItemTsoPqProps[] | undefined => {
    if (!tree) return undefined;

    return tree
      .slice()
      .sort((a, b) => (a.label && b.label ? a.label.localeCompare(b.label) : 0))
      .map((node) => {
        if (
          node.id === undefined ||
          node.id === null ||
          node.label === null ||
          node.label === undefined ||
          node.type === undefined
        ) {
          throw new Error('Invalid node: missing required properties');
        }

        const optionsLength = node.options?.length ?? 0;

        return {
          id: node.id ?? '',
          entityId: node.entityId ?? 0,
          label: node.label,
          type: typeMap[node.type],
          options: optionsLength > 0 ? convertDto(node.options) : undefined,
        };
      });
  };

  useEffect(() => {
    setOptions(convertDto(deviceHierarchy?.externalDtos));
  }, [deviceHierarchy]);

  useEffect(() => {
    if (deviceHierarchy && options) {
      const typeName = Object.keys(typeUrl).find((key) => typeUrl[key as keyof typeof typeUrl] === type);
      const id = flatTreeIds.find((item) => item.entityId === Number(deviceId) && item.type === typeName)?.id;
      id && setTreePath(findTreePath(options, id));
    }
  }, [deviceHierarchy, options]);

  useEffect(() => {
    const page = Object.entries(typeUrl).find(([, value]) => location.pathname.includes(value))?.[0];
    if (page) {
      setPageContent(page as PageContentType);
    }
  }, []);

  const treeProps = useFlattenTreeProps(options ?? [], ['id', 'type']) as {
    id: string;
    type: PageContentType;
  }[];

  const onSelectionHandler = (label: string, id: string, entityId: number) => {
    if (options) {
      setTreePath(findTreePath(options, id));

      const page = treeProps?.find((node) => node.id === id)?.type;

      setPageContent(page);
      page && navigate(`${RouteMap.ADMINISTRATION_ROUTE}/${typeUrl[page]}/${entityId}`);
    }
  };
  const handleTreeItemAdd = (type: string, id: string) => {
    const path = options && findTreePath(options, id);
    setTreePath(path);

    switch (type) {
      case 'substation':
        setModalContent('newMeasurmentPoint');
        break;
      case 'measurmentPoint':
        setModalContent('newMeasurmentDevice');
        break;
      default:
        return;
    }
    setOpenModal(true);
  };

  useEffect(() => {
    setSearchKey('label');
  }, []);

  if (deviceHierarchy === undefined) return;

  const atLeastOneNodeIsExpanded = expandedNodes?.length > 0;

  const sidePanel = (
    <SidePanelTsoPq
      sectionTitle={''}
      firstSlot={
        <Box
          sx={{
            flex: 1,
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
            justifyContent: 'flex-start',
          }}
        >
          <StyledSearchContainer>
            <SearchFieldTsoPq
              customwidth={'28.375rem'}
              searchQuery={searchTerm}
              searchKey={searchKey}
              handleChange={value => dispatch(setSearchTerm(value.toString()))}
              resetSearch={resetSearch}
            />
            <CollapseButtonTsoPq
              key={expandedNodes?.length ?? 0}
              tooltip={
                atLeastOneNodeIsExpanded
                  ? t('management.menu.collapseButton.tooltip.collapse')
                  : t('management.menu.collapseButton.tooltip.expand')
              }
              expanded={atLeastOneNodeIsExpanded}
              setExpanded={() => dispatch(setExpandedNodes(atLeastOneNodeIsExpanded ? [] : treeIds))}
            />
            <PermissionGate roles={userOrSupportRestriction}>
              <IconButtonTsoPq
                tooltip={t('management.substation.tooltip.createSubstation')}
                buttonType="filledPrimary"
                icon={faPlus}
                sizeVariant={'m'}
                onClick={() => {
                  setModalContent('newSubstation');
                  setOpenModal(true);
                }}
              />
            </PermissionGate>
          </StyledSearchContainer>
          <TreeItemViewTspPq
            onSelection={onSelectionHandler}
            options={options}
            buttonAction={handleTreeItemAdd}
          />
        </Box>
      }
    />
  );

  const content = (
    <StyledContentWrapper>
      <StyledContent>
        <StyledHeaderContainer>
          <StyledContentTitle>
            <TypographyTsoPq typographyType="pageTitleBold">{t('management.menu.title')}</TypographyTsoPq>
          </StyledContentTitle>
        </StyledHeaderContainer>
        <StyledManagementModals>
          {treePath ? (contentMap.page[pageContent as keyof typeof contentMap.page]) : (
            <ImagePlaceholderTsoPq
              title={t('management.placeholderTitle')}
              description={t('management.placeholderDescription')}
            />
          )}
          {openModal && (
            <ModalDialogTsoPq modalState={openModal}>
              {contentMap.modal[modalContent as keyof typeof contentMap.modal]}
            </ModalDialogTsoPq>
          )}
        </StyledManagementModals>
      </StyledContent>
    </StyledContentWrapper>
  );

  return <SidePanelPageLayoutTsoPq sidePanel={sidePanel} content={content} />;
};

export default ManagementPage;
